<template>
  <section
    v-if="isFlagReady"
    class="AuthFormView"
    data-testid="form"
  >
    <template v-if="!isInAppBrowser">
      <UIText
        class="AuthFormView__label"
        weight="Medium"
        size="Small"
      >
        <template v-if="variant === 'Login'">
          Welcome back
        </template>
        <template v-else>
          Create your free account
        </template>
      </UIText>

      <OAuthButtonView
        provider="Google"
        data-testid="googleButton"
        v-bind="{variant}"
      />
      <OAuthButtonView
        v-if="isAppleAuthEnabled"
        provider="Apple"
        data-testid="appleButton"
        v-bind="{variant}"
      />
      <OAuthButtonView
        v-if="isFacebookAuthEnabled"
        provider="Facebook"
        data-testid="facebookButton"
        v-bind="{variant}"
      />

      <div class="AuthFormView__separator">
        <UIText
          weight="Medium"
          size="XSmall"
          class="AuthFormView__separatorLabel"
        >
          or
        </UIText>

        <hr class="AuthFormView__separatorRule">
      </div>
    </template>

    <EmailFormView
      v-bind="{variant}"
      @send="$emit('send', $event)"
    />

    <UIText
      weight="Medium"
      size="Small"
      class="AuthFormView__loginOrSignupLink"
    >
      <template
        v-if="variant === 'Login'"
      >
        Need an account?
        <RouterLink
          :to="{ name: RouteName.signup, query: route.query }"
        >
          Sign up for free
        </RouterLink>
      </template>

      <template
        v-else
      >
        Already have an account?
        <RouterLink :to="{ name: RouteName.login, query: route.query }">
          Sign in
        </RouterLink>
      </template>
    </UIText>
  </section>
</template>

<script lang="ts" setup>
import UIText from "@cosine/components/UIText.vue";
import useFlag from "@cosine/composables/useFlag";
import { FlagKey } from "@cosine/composables/useFlag.types";
import { RouteName } from "@cosine/routes";
import EmailFormView from "@cosine/views/auth/EmailFormView.vue";
import { AuthFormVariant } from "@cosine/views/auth/AuthFormView.types";
import OAuthButtonView from "@cosine/views/auth/OAuthButtonView.vue";
import { useRoute } from "vue-router";

defineEmits<{
  send: [{ email: string }],
}>();

defineProps<{
  variant: AuthFormVariant,
}>();

const route = useRoute();

const {
  isFlagReady,
  flag: isAppleAuthEnabled,
} = useFlag(FlagKey.EnableAppleAuth, false);

const {
  flag: isFacebookAuthEnabled,
} = useFlag(FlagKey.EnableFacebookAuth, false);

const isInAppBrowser = /LinkedIn|Instagram/i.test(navigator.userAgent);
</script>

<style lang="scss" scoped>
.AuthFormView {
  display: grid;
  gap: 10px;
  align-content: start;
  padding: 24px;
}

.AuthFormView__label {
  margin-bottom: 8px;
}

.AuthFormView__separator {
  display: grid;
  place-items: center;
  margin-block: 8px;
}

.AuthFormView__separatorLabel {
  position: relative;
  z-index: 1;

  grid-column: 1 / 2;
  grid-row: 1 / 2;
  padding-inline: 8px;

  background: var(--colorSwissGrey50);
  color: var(--colorSwissAlphaBlack400);
}

.AuthFormView__separatorRule {
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  width: 100%;

  border: none;
  border-top: 1px solid var(--colorSwissGrey100);
}

.AuthFormView__loginOrSignupLink {
  margin: 16px auto;

  @media (display-mode: standalone) {
    margin-bottom: 40px;
  }
}
</style>
